import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { apiClient } from '@/lib/api';
import { useAuthStore } from '@/store/authStore';
import Button from '@components/ui/Button';
import { Input } from '@components/ui/Input';
import { Label } from '@components/ui/Label';
import PasswordInput from '@components/forms/PasswordInput';
import PhoneInput from '@components/forms/PhoneInput';
import { Alert, AlertDescription } from '@components/ui/Alert';
import { RiUserLine, RiPhoneLine, RiBuildingLine, RiArrowRightLine, RiAlertLine, RiGoogleLine, RiMicrosoftLine } from '@remixicon/react';
import { toast } from 'sonner';
import { decodeData } from '@/utils';

export default function TeamRegistration() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteData = decodeData(searchParams.get('d'));
  const [currentStep, setCurrentStep] = useState('register');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [loadingOrgs, setLoadingOrgs] = useState(true);
  const [user, setUser] = useState(null);

  console.log("inviteData", inviteData);

  // Registration state
  const [email, setEmail] = useState(inviteData.email || '');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [success, setSuccess] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const cooldownRef = useRef(null);

  // Profile state
  const [formData, setFormData] = useState({
    firstName: inviteData.firstName || '',
    lastName: inviteData.lastName || '',
    phone: '',
    countryCode: '+1',
    role: inviteData.role || 'user',
    orgId: inviteData.org_id || '',
  });

  useEffect(() => {
    return () => clearInterval(cooldownRef.current);
  }, []);

  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        setLoadingOrgs(true);
        const orgs = await apiClient.getOrganizations();
        setOrganizations(orgs);
      } catch (err) {
        console.error('Failed to fetch organizations:', err);
        toast.error('Failed to load organizations. Please refresh the page.');
      } finally {
        setLoadingOrgs(false);
      }
    };
    fetchOrganizations();
  }, []);

  const startCooldown = (seconds) => {
    setResendCooldown(seconds);
    clearInterval(cooldownRef.current);
    cooldownRef.current = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(cooldownRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleSignUp = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    setError(null);
    setSuccess(false);

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }

    if (!acceptTerms) {
      setError("You must accept the terms and conditions");
      return;
    }

    setLoading(true);

    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/register/team?${searchParams.toString()}`,
        },
      })

      if (error) throw error;

      if (data.user) {
        if (data.user.identities && data.user.identities.length === 0) {
          setError('User already registered. Please sign in.');
        } else if (data.user.confirmed_at) {
          setUser(data.user);
          setCurrentStep('profile');
        } else {
          setSuccess(true);
          startCooldown(60);
        }
      };

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setLoading(true);
    setError(null);
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email,
        options: {
          emailRedirectTo: `${window.location.origin}/register/team?${searchParams.toString()}`,
        },
      });
      if (error) throw error;
      startCooldown(60);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleNextAfterVerification = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (error) {
        toast.warning('Please confirm your email address before continuing.');
        return;
      }
      setUser(data.user);
      setCurrentStep('profile');
    } catch {
      toast.warning('Please confirm your email address before continuing.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleProfileSubmit = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    setLoading(true);

    try {
      // Register user in the gtj schema
      await apiClient.registerUser({
        user_id: user.id,
        email: user.email,
        org_id: formData.orgId,
        role: formData.role,
        first_name: formData.firstName,
        last_name: formData.lastName,
        phone: formData.phone ? `${formData.countryCode}${formData.phone}` : ''
      });

      await useAuthStore.getState().fetchUserProfile();

      navigate('/dashboard');
    } catch (err) {
      console.error('Profile setup error:', err);
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center py-4">
      <div className="max-w-md w-full flex flex-col flex-1">
        {/* Header */}
        <div className="text-center lg:text-left mb-6">
          <h2 className="text-3xl font-bold text-primary-500">
            {currentStep === 'register'
              ? (success ? "Verify Email" : `You've been invited to join ${inviteData.company}`)
              : "Complete Your Profile"}
          </h2>
          {currentStep === 'profile' && (
            <p className="text-gray-600 mt-2">
              Please provide some additional information to complete your registration.
            </p>
          )}
        </div>

        {/* Error Alert */}
        {error && (
          <Alert variant="destructive" className="border-red-200 bg-red-50 mb-3">
            <RiAlertLine className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="flex-1">
          {/* Registration Step */}
          {currentStep === 'register' && (
            <>
              {/* Verify Email Screen */}
              {success && (
                <div className="space-y-4">
                  <p className="text-sm text-gray-600">
                    We've sent an email to <span className="font-bold text-gray-900">{email}</span>
                  </p>
                  <p className="text-sm text-gray-600">
                    Recovery link will expire in 2 minutes.
                  </p>
                  <p className="text-sm text-gray-600">
                    Open the email and click on the link provided to verify your email address - come back here and tap next below when you're done.
                  </p>
                  <button
                    type="button"
                    onClick={handleResendVerification}
                    disabled={loading || resendCooldown > 0}
                    className="text-sm text-primary-500 hover:underline font-medium disabled:opacity-50 disabled:no-underline"
                  >
                    {resendCooldown > 0
                      ? `Resend Verification Email (${resendCooldown}s)`
                      : 'Resend Verification Email'}
                  </button>
                </div>
              )}

              {/* Sign Up Form */}
              {!success && (
                <div className="space-y-4">
                  <p className="text-sm">You are signing up as <span className='font-medium'>{inviteData.email}</span></p>
                  <form onSubmit={handleSignUp} className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label htmlFor="firstName">First Name <span className="text-[#0C3E71]"> *</span></Label>
                        <Input
                          id="firstName"
                          type="text"
                          placeholder="First name"
                          value={formData.firstName}
                          onChange={() => handleChange('firstName')}
                          required
                          disabled={loading}
                          className="h-11 !bg-white !border-2 !border-border-light !text-slate-500 !rounded-lg"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="lastName">Last Name <span className="text-[#0C3E71]"> *</span></Label>
                        <Input
                          id="lastName"
                          type="text"
                          placeholder="Last name"
                          value={formData.lastName}
                          onChange={() => handleChange('lastName')}
                          required
                          disabled={loading}
                          className="h-11 !bg-white !border-2 !border-border-light !text-slate-500 !rounded-lg"
                        />
                      </div>
                    </div>

                    <PasswordInput
                      id="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      required={true}
                      disabled={loading}
                      label="Password"
                      showLabel={true}
                      showValidation={true}
                    />

                    <PasswordInput
                      id="confirmPassword"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="••••••••"
                      required={true}
                      disabled={loading}
                      label="Confirm Password"
                      showLabel={true}
                      showValidation={false}
                    />

                    <PhoneInput
                      countryCode={formData.countryCode}
                      phone={formData.phone}
                      onCountryCodeChange={(code) => setFormData((prev) => ({ ...prev, countryCode: code }))}
                      onPhoneChange={(phone) => setFormData((prev) => ({ ...prev, phone }))}
                      required
                    />

                    <div className="flex items-start space-x-2">
                      <input
                        type="checkbox"
                        id="terms"
                        checked={acceptTerms}
                        onChange={(e) => setAcceptTerms(e.target.checked)}
                        disabled={loading}
                        className="mt-0.5 w-4 h-4 text-primary-500 border-gray-300 rounded focus:ring-primary-500"
                      />
                      <label
                        htmlFor="terms"
                        className="text-sm text-slate-600 leading-tight cursor-pointer"
                      >
                        I accept the{" "}
                        <a href="#" className="text-[#0C3E71] hover:underline">
                          Terms and Conditions
                        </a>{" "}
                        and{" "}
                        <a href="#" className="text-[#0C3E71] hover:underline">
                          Privacy Policy
                        </a>
                      </label>
                    </div>
                  </form>
                </div>
              )}
            </>
          )}

          {/* Profile Step */}
          {currentStep === 'profile' && (
            <form onSubmit={handleProfileSubmit} className="space-y-6">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="firstName">First Name <span className="text-red-500">*</span></Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                      <RiUserLine size={16} />
                    </span>
                    <Input
                      id="firstName"
                      name="firstName"
                      type="text"
                      placeholder="Enter your first name"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                      disabled={loading}
                      className="h-11 pl-10 bg-white border-2 border-gray-200 text-gray-700 rounded-lg focus:none"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="lastName">Last Name <span className="text-red-500">*</span></Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                      <RiUserLine size={16} />
                    </span>
                    <Input
                      id="lastName"
                      name="lastName"
                      type="text"
                      placeholder="Enter your last name"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                      disabled={loading}
                      className="h-11 pl-10 bg-white border-2 border-gray-200 text-gray-700 rounded-lg focus:outline-none"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                    <RiPhoneLine size={16} />
                  </span>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    placeholder="Enter your phone number (optional)"
                    value={formData.phone}
                    onChange={handleChange}
                    disabled={loading}
                    className="h-11 pl-10 bg-white border-2 border-gray-200 text-gray-700 rounded-lg focus:outline-none"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="orgId">Organization <span className="text-red-500">*</span></Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 z-10">
                    <RiBuildingLine size={16} />
                  </span>
                  <select
                    id="orgId"
                    name="orgId"
                    value={formData.orgId}
                    onChange={handleChange}
                    required
                    disabled={true}
                    className="h-11 w-full pl-10 pr-4 bg-white border-2 border-gray-200 text-gray-700 rounded-lg focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed appearance-none"
                  >
                    <option value="">
                      {loadingOrgs ? 'Loading organizations...' : 'Select your organization'}
                    </option>
                    {organizations.map((org) => (
                      <option key={org.organization_id} value={org.organization_id}>
                        {org.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </form>
          )}
        </div>

        {/* Bottom Buttons */}
        <div className="flex space-x-4 mt-auto pt-6">
          {currentStep === 'register' && (
            <Button
              type="button"
              className="flex-1 !font-normal bg-primary-500 hover:bg-primary-600 disabled:bg-[#F7F7F7] disabled:border disabled:border-border-light disabled:text-gray-400"
              disabled={loading}
              onClick={success ? handleNextAfterVerification : handleSignUp}
            >
              {success ? "Next" : "Create account"}
            </Button>
          )}

          {currentStep === 'profile' && (
            <Button
              type="button"
              className="flex-1 w-full !font-normal bg-primary-500 hover:bg-primary-600 disabled:bg-[#F7F7F7] disabled:border disabled:border-border-light disabled:text-gray-400"
              disabled={loading || !formData.firstName || !formData.lastName || !formData.orgId}
              onClick={handleProfileSubmit}
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Saving...
                </div>
              ) : (
                <div className="flex items-center justify-center">
                  Continue
                  <RiArrowRightLine className="ml-2 h-4 w-4" />
                </div>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
